/**
 * Logistics GPS Tracking (L4) - Historical Replay
 * Version: 1.0.0
 * Description:
 *   Replay historical GPS trails for any past delivery run.
 *   Useful for auditing driver paths and verifying delivery speeds/idling.
 */

import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Container,
  Divider,
  Grid,
  Stack,
  Typography,
  Chip,
  Avatar,
  Slider,
  Paper,
  IconButton,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  MenuItem,
  TextField
} from '@mui/material';
import {
  History as HistoryIcon,
  PlayArrow as PlayIcon,
  Pause as PauseIcon,
  FastForward as FastForwardIcon,
  Speed as SpeedIcon,
  Map as MapIcon,
  Refresh as RefreshIcon,
  Timeline as TrailIcon,
  PinDrop as StopIcon
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { logisticsAPI } from '../../api';
import dayjs from 'dayjs';

// --- Types ---
interface GPSLog {
  id: string;
  lat: number;
  lng: number;
  speed: number;
  recorded_at: string;
}

interface RunSelectItem {
  id: string;
  driver_name: string;
  run_date: string;
  vehicle_number: string;
}

// --- Component ---
export default function LogisticsGPS() {
  const { enqueueSnackbar } = useSnackbar();
  const [runs, setRuns] = useState<RunSelectItem[]>([]);
  const [selectedRun, setSelectedRun] = useState<string>('');
  const [trail, setTrail] = useState<GPSLog[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Replay State
  const [isPlaying, setIsPlaying] = useState(false);
  const [timelineIndex, setTimelineIndex] = useState(0);

  // Fetch Available Runs
  const fetchRuns = async () => {
    try {
      const data = await logisticsAPI.listRuns();
      setRuns(data || []);
    } catch (error) {
      enqueueSnackbar('Failed to fetch past trips', { variant: 'error' });
    }
  };

  useEffect(() => {
    fetchRuns();
  }, [enqueueSnackbar]);

  // Fetch Trail
  const fetchTrail = async (runId: string) => {
    if (!runId) return;
    try {
      setLoading(true);
      const data = await logisticsAPI.getRunTrail(runId);
      setTrail(data || []);
      setTimelineIndex(0);
      setIsPlaying(false);
    } catch (error) {
      enqueueSnackbar('Failed to fetch trail data', { variant: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const handleRunChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const runId = e.target.value;
    setSelectedRun(runId);
    fetchTrail(runId);
  };

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 3, color: 'primary.main' }}>
        GPS Historical Replay
      </Typography>

      <Grid container spacing={3}>
        {/* Left Column: Selection & Controls */}
        <Grid item xs={12} md={3}>
           <Stack spacing={3}>
              <Card sx={{ p: 2, borderRadius: 2 }}>
                 <Typography variant="subtitle2" gutterBottom>1. Select Trip</Typography>
                 <TextField
                    select
                    fullWidth
                    label="Past Trips"
                    value={selectedRun}
                    onChange={handleRunChange}
                    size="small"
                 >
                    {runs.map((r) => (
                       <MenuItem key={r.id} value={r.id}>
                          {dayjs(r.run_date).format('DD MMM')} • {r.driver_name}
                       </MenuItem>
                    ))}
                    {runs.length === 0 && <MenuItem disabled>No trips found</MenuItem>}
                 </TextField>
              </Card>

              {trail.length > 0 && (
                 <Card sx={{ p: 2, borderRadius: 2 }}>
                    <Typography variant="subtitle2" gutterBottom>2. Playback Controls</Typography>
                    <Stack spacing={2} mt={1}>
                       <Stack direction="row" spacing={1} justifyContent="center">
                          <IconButton onClick={() => setIsPlaying(!isPlaying)} color="primary">
                             {isPlaying ? <PauseIcon /> : <PlayIcon />}
                          </IconButton>
                          <IconButton><FastForwardIcon /></IconButton>
                       </Stack>
                       
                       <Box px={1}>
                          <Slider 
                             value={timelineIndex} 
                             max={trail.length - 1} 
                             onChange={(_, val) => setTimelineIndex(val as number)}
                             color="primary"
                          />
                       </Box>
                       
                       <Typography variant="caption" align="center" color="text.secondary">
                          {trail[timelineIndex]?.recorded_at ? dayjs(trail[timelineIndex].recorded_at).format('HH:mm:ss') : '--:--'}
                       </Typography>
                    </Stack>
                 </Card>
              )}

              {/* Trip Stats */}
              <Card sx={{ p: 2, borderRadius: 2 }}>
                 <Typography variant="subtitle2" gutterBottom>Trip Analytics</Typography>
                 {trail.length > 0 ? (
                    <Stack spacing={1}>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption">Total Pings</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{trail.length}</Typography>
                       </Box>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption">Max speed</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>{Math.max(...trail.map(t => t.speed)).toFixed(1)} km/h</Typography>
                       </Box>
                       <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="caption">Active Time</Typography>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                             {dayjs(trail[trail.length - 1].recorded_at).diff(dayjs(trail[0].recorded_at), 'minute')} mins
                          </Typography>
                       </Box>
                    </Stack>
                 ) : (
                    <Typography variant="body2" color="text.secondary">Select a run to view data.</Typography>
                 )}
              </Card>
           </Stack>
        </Grid>

        {/* Right Column: Visual Trail */}
        <Grid item xs={12} md={9}>
           <Card sx={{ 
              borderRadius: 2, 
              height: 700, 
              backgroundColor: 'rgba(0,0,0,0.02)',
              position: 'relative',
              overflow: 'hidden'
           }}>
              {loading ? (
                 <Box display="flex" justifyContent="center" alignItems="center" height="100%"><CircularProgress /></Box>
              ) : trail.length > 0 ? (
                 <Box sx={{ p: 4, textAlign: 'center', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                    <MapIcon sx={{ fontSize: 80, color: 'text.disabled', opacity: 0.1, mb: 2 }} />
                    <Typography variant="h6" color="text.disabled">Route Visualization</Typography>
                    <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 450, mx: 'auto' }}>
                       Historical trail mapping requires Google Maps API credentials. 
                       Once enabled, this view will render the exact path taken on {dayjs(runs.find(r=>r.id===selectedRun)?.run_date).format('DD MMM')} 
                       by {runs.find(r=>r.id===selectedRun)?.driver_name}.
                    </Typography>
                    
                    <Paper variant="outlined" sx={{ mt: 4, mx: 'auto', p: 2, maxWidth: 400, textAlign: 'left' }}>
                       <Typography variant="subtitle2" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                          <TrailIcon fontSize="small" color="primary" /> Ping Data (Current)
                       </Typography>
                       <Divider sx={{ mb: 2 }} />
                       <Stack spacing={1}>
                          <Typography variant="caption"><strong>Coordinate:</strong> {trail[timelineIndex].lat.toFixed(4)}, {trail[timelineIndex].lng.toFixed(4)}</Typography>
                          <Typography variant="caption"><strong>Speed:</strong> {trail[timelineIndex].speed.toFixed(1)} km/h</Typography>
                          <Typography variant="caption"><strong>Time:</strong> {dayjs(trail[timelineIndex].recorded_at).format('hh:mm:ss A')}</Typography>
                       </Stack>
                    </Paper>
                 </Box>
              ) : (
                 <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" height="100%">
                    <TrailIcon sx={{ fontSize: 60, color: 'text.disabled', opacity: 0.2, mb: 1 }} />
                    <Typography color="text.disabled">Select a past run to audit movement history.</Typography>
                 </Box>
              )}
           </Card>
        </Grid>
      </Grid>
    </Container>
  );
}
